import React from 'react';
import { Modal, ScrollView } from 'react-native';
import { Button, Text, View } from '../../../src/Components/Themed';
import { useColors } from '../../../src/constants/useColors';
import { Icons } from '../../../src/Components';

type HelpModalProps = {
    visible: boolean,
    mode: 'desktop' | 'mobile',
    insets: { top: number, bottom: number, left: number, right: number },
    onClose: () => void
}

const HelpModal = ({ visible, mode, insets, onClose }: HelpModalProps) => {
    const Colors = useColors();

    const HelpSection = ({ icon, title, description }: { icon: any, title: string, description: string }) => (
        <View transparent style={{ marginBottom: 20 }}>
            <View transparent style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 8 }}>
                {icon}
                <Text theme='h1' size={16} style={{ marginLeft: 10 }}>{title}</Text>
            </View>
            <Text theme='description' size={14} style={{ lineHeight: 20 }}>{description}</Text>
        </View>
    );

    return (
        <Modal
            visible={visible}
            animationType={mode == 'desktop' ? 'fade' : 'slide'}
            transparent
            style={{ flex: 1 }}>
            <View type='blur' style={mode == 'desktop' ? { flex: 1, justifyContent: 'center', alignItems: 'center' } : { flex: 1, justifyContent: 'flex-end', paddingBottom: insets.bottom }}>
                <View float style={mode == 'desktop' ? { width: 600, maxHeight: '80%', borderRadius: 22 } : { maxHeight: '90%', borderTopRightRadius: 22, borderTopLeftRadius: 22 }}>
                    <View type='header' style={{ padding: 20, borderTopRightRadius: 22, borderTopLeftRadius: 22, flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                        <View transparent style={{ flex: 1, flexDirection: 'row' }}>
                            <Icons.BettorEdgeIcon size={18} color={Colors.text.h1} />
                            <View transparent style={{ flex: 1, marginLeft: 10 }}>
                                <Text theme='h1' size={20}>How to Use Markets</Text>
                                <Button transparent style={{ alignSelf: 'flex-start', marginTop: 3, padding: 0 }} onPress={() => {
                                    const url = 'https://www.bettoredge.com/forum-help';
                                    if (typeof window !== 'undefined') {
                                        window.open(url, '_blank');
                                    } else {
                                        require('react-native').Linking.openURL(url);
                                    }
                                }}>
                                    <Text theme='action' size={12}>View full help article →</Text>
                                </Button>
                            </View>
                        </View>
                        <Button transparent onPress={onClose}>
                            <Icons.CloseIcon size={14} color={Colors.text.h1} />
                        </Button>
                    </View>

                    <ScrollView style={{ flex: 1 }}>
                        <View transparent style={{ padding: 20 }}>
                            {/* Introduction */}
                            <View transparent style={{ marginBottom: 25 }}>
                                <Text theme='h2' size={15} style={{ marginBottom: 10 }}>
                                    Welcome to BettorEdge Markets! Trade on your favorite teams and events with real-time odds.
                                </Text>
                            </View>

                            {/* Browsing Markets */}
                            <HelpSection
                                icon={<Icons.BettorEdgeIcon size={20} color={Colors.text.action} />}
                                title="Browse Markets"
                                description="View live markets organized by league. Use the league dropdown to switch between NBA, NFL, MLB, and more. Popular markets are shown by default with the most active trading."
                            />

                            {/* Search */}
                            <HelpSection
                                icon={<Icons.SearchIcon size={20} color={Colors.text.action} />}
                                title="Search Teams"
                                description="Click the search icon to find specific teams quickly. Search by team name or location. Click the X to clear your search and return to all markets."
                            />

                            {/* Placing Orders */}
                            <HelpSection
                                icon={<Icons.OrderIcon size={20} color={Colors.text.action} />}
                                title="View Odds & Details"
                                description="Each market shows current odds for both teams. Click on any game to see the full order book, recent trades, and detailed statistics. Odds update in real-time as trades happen."
                            />

                            {/* Trading */}
                            <HelpSection
                                icon={<Icons.EdgeCoinIcon size={20} color={Colors.text.action} />}
                                title="Start Trading"
                                description="Click on a market to open the full trading interface. Place buy or sell orders, view market depth, and track your positions. Sign in to start trading."
                            />

                            {/* Market Types */}
                            <HelpSection
                                icon={<Icons.TrophyIcon size={20} color={Colors.text.action} />}
                                title="Market Types"
                                description="Trade on game winners, point spreads, and player props. Each market settles based on official results. Check market rules before trading."
                            />

                            {/* Tips */}
                            <View float style={{ padding: 15, borderRadius: 12, marginTop: 10 }}>
                                <Text theme='h1' size={15} style={{ marginBottom: 10 }}>💡 Pro Tips</Text>
                                <Text theme='description' size={13} style={{ lineHeight: 18, marginBottom: 8 }}>
                                    • Markets are most active close to game time
                                </Text>
                                <Text theme='description' size={13} style={{ lineHeight: 18, marginBottom: 8 }}>
                                    • Watch for odds movements to spot trading opportunities
                                </Text>
                                <Text theme='description' size={13} style={{ lineHeight: 18, marginBottom: 8 }}>
                                    • Use the order book to see market depth and liquidity
                                </Text>
                                <Text theme='description' size={13} style={{ lineHeight: 18 }}>
                                    • Set price alerts to get notified of favorable odds
                                </Text>
                            </View>
                        </View>
                    </ScrollView>

                    <View type='footer' style={{ padding: 15, borderBottomLeftRadius: 22, borderBottomRightRadius: 22 }}>
                        <Button
                            title="Got it!"
                            type='success'
                            onPress={onClose}
                        />
                    </View>
                </View>
            </View>
        </Modal>
    );
};

export default HelpModal;
